import styled, { keyframes } from "styled-components";

const stepFadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(28px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const botFloat = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-10px) scale(1.02);
  }
`;

const botRun = keyframes`
  0%, 100% {
    transform: translateX(0) rotate(0deg);
  }
  25% {
    transform: translateX(8px) rotate(1deg);
  }
  75% {
    transform: translateX(-4px) rotate(-0.5deg);
  }
`;

const botPress = keyframes`
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  40% {
    transform: translateY(6px) scale(0.98);
  }
  60% {
    transform: translateY(2px) scale(0.99);
  }
`;

const shimmer = keyframes`
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(120%);
  }
`;

const imageAnimations = {
  float: botFloat,
  run: botRun,
  press: botPress,
};

const DtfInfoWrapper = styled.section`
  margin: 24px -20px 0;
  overflow: hidden;
`;

const WhatIsDtfSection = styled.div`
  background-color: #ebedf0;
  padding: 48px 24px;
  text-align: center;
`;

const WhatIsDtfInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const WhatIsDtfTitle = styled.h2`
  margin: 0 0 16px;
  font-family: "Inter800";
  font-size: 32px;
  line-height: 1.2;
  color: #18191f;

  @media (max-width: 768px) {
    font-size: 26px;
  }
`;

const WhatIsDtfDescription = styled.p`
  margin: 0;
  font-family: "Inter400";
  font-size: 16px;
  line-height: 1.7;
  color: #5f6368;
`;

const DtfProcessSection = styled.div`
  background-color: #ffffff;
  padding: 48px 24px 56px;
`;

const DtfProcessInner = styled.div`
  max-width: 1140px;
  margin: 0 auto;
`;

const ProcessHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 40px;
`;

const ProcessTitle = styled.h2`
  margin: 0;
  font-family: "Inter800";
  font-size: 28px;
  line-height: 1.2;
  color: #18191f;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const ProcessDivider = styled.span`
  display: inline-block;
  width: 1px;
  height: 28px;
  background-color: #d1d5db;
`;

const ProcessSubtitle = styled.p`
  margin: 0;
  font-family: "Inter400";
  font-size: 15px;
  line-height: 1.4;
  color: #6b7280;
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 32px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const ProcessStep = styled.div`
  text-align: center;
  opacity: 0;
  animation: ${stepFadeInUp} 0.7s ease forwards;
  animation-delay: ${({ $delay = 0 }) => $delay * 0.15}s;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`;

const StepImageWrap = styled.div`
  position: relative;
  margin-bottom: 20px;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #f3f4f6 0%, #e8edf3 100%);
  box-shadow: 0 4px 20px rgba(21, 148, 219, 0.08);
  transition: transform 0.35s ease, box-shadow 0.35s ease;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      105deg,
      transparent 40%,
      rgba(255, 255, 255, 0.35) 50%,
      transparent 60%
    );
    transform: translateX(-120%);
    animation: ${shimmer} 4s ease-in-out infinite;
    animation-delay: ${({ $delay = 0 }) => 1.2 + $delay * 0.4}s;
    pointer-events: none;
  }

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(21, 148, 219, 0.16);
  }

  img {
    display: block;
    width: 100%;
    height: auto;
    aspect-ratio: 4 / 3;
    object-fit: cover;
    transform-origin: center bottom;
    animation: ${({ $animation = "float" }) => imageAnimations[$animation] || botFloat}
      4s ease-in-out infinite;
    animation-delay: ${({ $delay = 0 }) => 0.8 + $delay * 0.2}s;
  }

  @media (prefers-reduced-motion: reduce) {
    &::after {
      animation: none;
    }

    img {
      animation: none;
    }

    &:hover {
      transform: none;
    }
  }
`;

const StepTitle = styled.h3`
  margin: 0 0 12px;
  font-family: "Inter600";
  font-size: 20px;
  line-height: 1.35;
  color: #18191f;
`;

const StepDescription = styled.p`
  margin: 0 auto;
  max-width: 320px;
  font-family: "Inter400";
  font-size: 15px;
  line-height: 1.65;
  color: #6b7280;
`;

export {
  DtfInfoWrapper,
  WhatIsDtfSection,
  WhatIsDtfInner,
  WhatIsDtfTitle,
  WhatIsDtfDescription,
  DtfProcessSection,
  DtfProcessInner,
  ProcessHeader,
  ProcessTitle,
  ProcessDivider,
  ProcessSubtitle,
  ProcessSteps,
  ProcessStep,
  StepImageWrap,
  StepTitle,
  StepDescription,
};
